import moment, { unitOfTime } from 'moment';

import 'moment-timezone';

import { getUserProfile } from './AuthUtil';

export const toInputDateTime = (d: string): string => {
  if (d) {
    const local: Date = new Date(d);
    local.setMinutes(local.getMinutes() - local.getTimezoneOffset());

    return local.toISOString().slice(0, 16);
  }

  return '';
};

export const fromInputDateTime = (d: string) => {
  if (d) {
    const local: Date = new Date(d);

    return local.toISOString();
  }

  return '';
};

export const fromISOStringToDate = (str: string) => {
  if (str) {
    return str.slice(0, 10);
  }

  return '';
};

export const fromISOStringToDateTimeString = (ISOString: string) => {
  if (ISOString) {
    const format = 'MMMM DD, YYYY h:mm A';
    const timezone = getTimezone();

    return timezone ? moment(ISOString).tz(timezone).format(format) : moment.utc(ISOString).format(format);
  }

  return 'Invalid Date Format';
};

export const getTimezone = () => (getUserProfile() ? getUserProfile().agencyTimezone : null);

export const fromISOStringToFormattedDateString = (ISOString: string | Date, format: string = 'MMMM D, YYYY') => {
  const timezone = getTimezone();
  if (ISOString && moment(ISOString, moment.ISO_8601).isValid()) {
    return timezone ? moment(ISOString).tz(timezone).format(format) : moment.utc(ISOString).format(format);
  }

  return 'Invalid Date Format';
};

export const ISOStringToShortDate = (ISOString: string | Date, format: string = 'MM/DD/YYYY') => {
  const timezone = getTimezone();
  if (ISOString && moment(ISOString, moment.ISO_8601).isValid()) {
    return timezone ? moment(ISOString).tz(timezone).format(format) : moment.utc(ISOString).format(format);
  }

  return 'Invalid Date Format';
};

export const getStartOfDate = (date = moment()) => {
  const timezone = getTimezone();

  return timezone ? date.tz(timezone).startOf('day') : date.utc().startOf('day');
};

export const getDateAfterString = (count: number, unit: DateUnit, fromDate = moment()) => {
  const timezone = getTimezone();

  return timezone
    ? moment(fromDate).tz(timezone).add(count, unit).format()
    : moment(fromDate).utc().add(count, unit).format();
};

export const getCurrentDateTime = () => {
  const timezone = getTimezone();

  return timezone ? moment().tz(timezone).format() : moment().utc().format();
};

export const getDateAfter = (count: number, unit: DateUnit, fromDate = moment()) => {
  const timezone = getTimezone();

  return timezone ? moment(fromDate).tz(timezone).add(count, unit) : moment(fromDate).utc().add(count, unit);
};

export const getTodaysDate = (format: string = 'MM/DD/YYYY') => {
  const timezone = getTimezone();

  return moment().tz(timezone).format(format);
};

export const getAgencyTime = () => {
  const timezone = getTimezone();

  return moment().tz(timezone);
};

const reportsFilterDateRange = (
  reportsFilter: ReportsFilter,
  inclusivity: 'start' | 'end',
  options?: { month: number; date: number }
) => {
  const currentAgencyTime = getAgencyTime();
  switch (reportsFilter) {
    case ReportsFilter.LAST_SEVEN_DAYS:
      return inclusivity === 'start'
        ? currentAgencyTime.subtract(7, 'days').startOf('day')
        : currentAgencyTime.subtract(1, 'days').endOf('day');

    case ReportsFilter.MTD:
      return inclusivity === 'start' ? currentAgencyTime.startOf('month') : currentAgencyTime.endOf('day');

    case ReportsFilter.LAST_MONTH:
      return inclusivity === 'start'
        ? getDateAfter(-1, DateUnit.MONTHS).startOf('month').toDate()
        : getDateAfter(-1, DateUnit.MONTHS).endOf('month').toDate();

    case ReportsFilter.YTD_CALENDAR:
      return inclusivity === 'start' ? currentAgencyTime.startOf('year') : currentAgencyTime.endOf('day');

    case ReportsFilter.YTD_FISCAL:
      if (options) {
        if (inclusivity === 'start') {
          const { month, date } = options;
          /*
           * Another var to store agency time
           * as the other one changes due to set
           */
          const tempCurrentAgencyTime = getAgencyTime();
          const fiscalStartDate = tempCurrentAgencyTime
            .set({
              month,
              date,
            })
            .startOf('day');
          let currentYear = currentAgencyTime.year();

          if (fiscalStartDate.isAfter(currentAgencyTime)) {
            currentYear--;
          }

          return currentAgencyTime.set({ year: currentYear, month, date }).startOf('day');
        }
      }

      return currentAgencyTime.endOf('day');
  }
};

export const getReportsFilterValue = (
  reportsFilter: ReportsFilter,
  inclusivity: 'start' | 'end',
  options?: { month: number; date: number }
) => reportsFilterDateRange(reportsFilter, inclusivity, options).toISOString();

export const getCaseFilterDate = (
  reportsFilter: ReportsFilter,
  options?: { month: number; date: number },
  format: string = 'MM/DD/YYYY'
) => {
  if (reportsFilter === ReportsFilter.LAST_SEVEN_DAYS) {
    return DateFilterType.LAST_SEVEN_DAYS;
  }

  const startDate = moment(reportsFilterDateRange(reportsFilter, 'start', options));
  const endDate = moment(reportsFilterDateRange(reportsFilter, 'end', options));

  return `${startDate.format(format)}-${endDate.format(format)}`;
};

export const isSame = (date, anotherDate, param: unitOfTime.StartOf = 'date') =>
  moment(date).isSame(anotherDate, param);

export const isAfter = (date, anotherDate, param: unitOfTime.StartOf = 'date') =>
  moment(date).isAfter(anotherDate, param);

export const isBefore = (date, anotherDate, param: unitOfTime.StartOf = 'date') =>
  moment(date).isBefore(anotherDate, param);

export const getPrivateCSDPDateString = (ISOString: string | Date, format: string = 'MMMM D, YYYY') => {
  if (ISOString && moment(ISOString, moment.ISO_8601).isValid()) {
    return moment(ISOString).format(format);
  }

  return 'Invalid Date Format';
};

export const getTimeDifference = (startTime: moment.Moment, endTime: moment.Moment) => {
  const hours = endTime.diff(startTime, 'hours');
  const minutes = endTime.diff(startTime, 'minutes');
  const formattedMinutes = (minutes - hours * 60) / 60;
  const formattedHours = hours + formattedMinutes;

  return formattedHours.toString();
};

export const toMiddayUTC = (dateStr: string): string => {
  if (!dateStr) return dateStr;
  const timezone = getTimezone();
  const localDate = timezone
    ? moment(dateStr).tz(timezone).format('YYYY-MM-DD')
    : moment.utc(dateStr).format('YYYY-MM-DD');
  return moment.utc(localDate, 'YYYY-MM-DD').add(12, 'hours').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
};

export const formatDateEST = (isoDate: string): string => {
  const date = new Date(isoDate);
  const estDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  const month = (estDate.getMonth() + 1).toString().padStart(2, '0');
  const day = estDate.getDate().toString().padStart(2, '0');
  const year = estDate.getFullYear();

  let hours = estDate.getHours();
  const minutes = estDate.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${month}/${day}/${year} at ${hours}:${minutes}${ampm}`;
};
